"use client"

import BaseImage from "@/app/(site)/_components/Atoms/BaseImage"
import Text from "@/app/(site)/_components/Atoms/Text"
import FullscreenImagePopup from "@/app/(site)/_components/Modules/ProductDetail/FullscreenImage"
import { Badge } from "@/app/(site)/_components/Molecules/Badge"
import { Media } from "@/lib/content-service/types/ContentServiceProduct"
import placeholder from "@assets/images/placeholder.png"
import React, { useEffect, useRef, useState } from "react"

type ProductImageGalleryProps = {
    media?: Media
    combinedBadges?: string[]
    available?: boolean
    productName: string
}

export default function ProductImageGallery({
    media,
    combinedBadges,
    available,
    productName
}: ProductImageGalleryProps) {
    const slicedImages =
        Array.isArray(media?.small) && media?.small?.length
            ? typeof window !== "undefined"
                ? media.standard.slice(0, 4)
                : media.small.slice(0, 4)
            : []

    const hasOneOrLessImage = media && media.standard.length <= 1
    const serverSafeImageSrc =
        media?.small?.map((image: any) => image.url || placeholder) || []

    const [imageSrc, setImageSrc] = useState<string[]>(serverSafeImageSrc)
    const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null)
    const [currentIndex, setCurrentIndex] = useState(0)
    const [offsetX, setOffsetX] = useState(0)

    const imageContainerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        setOffsetX(-100 * currentIndex)
    }, [currentIndex])

    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth
            const newImageSrc =
                width <= 1440
                    ? media?.small.map(item => item.url)
                    : media?.standard.map(item => item.url)
            setImageSrc(newImageSrc || [])
        }

        if (typeof window !== "undefined") {
            handleResize()
            window.addEventListener("resize", handleResize)
            return () => {
                window.removeEventListener("resize", handleResize)
            }
        }
    }, [media])

    const handleSwipe = (direction: "left" | "right") => {
        if (direction === "left" && currentIndex < slicedImages.length - 1) {
            setCurrentIndex(currentIndex + 1)
        } else if (direction === "right" && currentIndex > 0) {
            setCurrentIndex(currentIndex - 1)
        }
    }

    const onTouchStart = (e: React.TouchEvent) => {
        const touchDown = e.touches[0].clientX

        const onTouchEnd = (event: TouchEvent) => {
            const touchUp = event.changedTouches[0].clientX
            if (touchDown > touchUp + 5) {
                handleSwipe("left")
            } else if (touchDown < touchUp - 5) {
                handleSwipe("right")
            }
            imageContainerRef.current?.removeEventListener("touchend", onTouchEnd)
        }

        imageContainerRef.current?.addEventListener("touchend", onTouchEnd)
    }

    const getGridTemplate = () => {
        switch (media?.small.length) {
            case 1:
                return "grid-cols-1"
            case 2:
                return "grid-cols-2"
            default:
                return "grid-cols-2 md:grid-cols-2 col-span-2 md:grid-rows-2"
        }
    }

    return (
        <div
            className={`relative col-span-2 grid grid-cols-1 gap-24 overflow-hidden ${
                hasOneOrLessImage ? "col-span-3" : "col-span-4"
            } `}
        >
            {/* Mobile view */}
            {slicedImages.length > 1 && (
                <Text
                    size="small"
                    weight="regular"
                    className="absolute right-16 top-16 z-10 block md:hidden"
                >
                    {currentIndex + 1}/{slicedImages.length}
                </Text>
            )}
            <div
                ref={imageContainerRef}
                className="flex transition-transform duration-300 md:hidden"
                onTouchStart={onTouchStart}
                style={{ transform: `translateX(${offsetX}%)` }}
            >
                {Array.isArray(combinedBadges) && combinedBadges.length > 0 && (
                    <div className="absolute left-0 right-0 top-0 z-[5] flex flex-wrap gap-2">
                        {combinedBadges.map((badge, index) => (
                            <Badge key={index} badge={badge} />
                        ))}
                    </div>
                )}
                {slicedImages.length > 0 ? (
                    slicedImages.map((image, index) => (
                        <div
                            key={`${index}-${imageSrc[index]}`}
                            className="relative aspect-square min-w-full bg-[#F4F5F5]"
                            role="button"
                            onClick={() => setSelectedImageIndex(index)}
                        >
                            <BaseImage
                                src={imageSrc[index] ?? placeholder.src}
                                fill
                                alt={`Product image of ${productName}`}
                                loading={index === 0 ? "eager" : "lazy"}
                                priority={index === 0}
                                decoding={index === 0 ? "async" : "sync"}
                                className={
                                    available ? "object-cover" : "object-cover opacity-30"
                                }
                            />
                        </div>
                    ))
                ) : (
                    <div className="relative aspect-square min-w-full bg-[#F4F5F5]">
                        <BaseImage
                            src={placeholder.src}
                            alt={"Placeholder image"}
                            fill
                            loading="eager"
                            priority={true}
                            className={
                                available ? "object-cover" : "object-cover opacity-30"
                            }
                        />
                    </div>
                )}
            </div>
            {/* Desktop view */}
            <div className={`relative hidden md:block`}>
                {Array.isArray(combinedBadges) && combinedBadges.length > 0 && (
                    <div className="absolute left-0 right-0 top-0 z-[5] flex w-300 flex-wrap gap-2 xl:w-400 2xl:w-600 3xl:w-700">
                        {combinedBadges.map((badge, index) => (
                            <Badge key={index} badge={badge} />
                        ))}
                    </div>
                )}
                <div
                    className={`col-span-2 grid w-full grid-cols-2 gap-24 ${getGridTemplate()}`}
                >
                    {slicedImages.length > 0 ? (
                        slicedImages.map((image, index) => {
                            return (
                                <div
                                    key={index}
                                    className={` ${hasOneOrLessImage ? "col-span-2 row-span-2" : "col-row-1 col-span-1"} relative aspect-square cursor-pointer bg-[#F4F5F5]`}
                                    role="button"
                                    onClick={() => setSelectedImageIndex(index)}
                                >
                                    <BaseImage
                                        src={imageSrc[index] ?? placeholder.src}
                                        fill
                                        alt={`Product image of ${productName}`}
                                        loading="eager"
                                        priority={true}
                                        className={
                                            !available
                                                ? "object-cover opacity-30"
                                                : "object-cover"
                                        }
                                    />
                                </div>
                            )
                        })
                    ) : (
                        <div className="relative col-span-2 row-span-2 aspect-square cursor-pointer bg-[#F4F5F5]">
                            <BaseImage
                                src={placeholder.src}
                                fill
                                priority={true}
                                loading="eager"
                                alt="Placeholder image"
                                className={
                                    !available
                                        ? "object-cover opacity-30"
                                        : "object-cover"
                                }
                            />
                        </div>
                    )}
                </div>
            </div>
            {/* Fullscreen Popup */}
            {selectedImageIndex !== null && media && (
                <FullscreenImagePopup
                    media={media}
                    selectedImageIndex={selectedImageIndex}
                    onClose={() => setSelectedImageIndex(null)}
                    productName={productName}
                />
            )}
        </div>
    )
}
